import { LeadsQueryResultDto } from "./dtos";
import { useQuery } from "@tanstack/react-query";
import { Product } from "types/product";
import { GetAllLeadProducts, GetAllLeads } from "constants/reactQueryKey";
import { AnfragenManagerClient, get } from "lib/httpService";

// Angepasster Hook, der Mock-Daten zurückliefert
export const useGetAllLeads = (
  pageSize: number,
  pageIndex: number,
  nameSearchText: string,
  anfragenNumberSearchText: string,
  leadStates: number[],
  types: number[],
  products: number[],
  sortField: string,
  sortOrder: "asc" | "desc",
  options = {}
) => {
  return useQuery(
    [
      GetAllLeads,
      pageSize,
      pageIndex,
      nameSearchText,
      anfragenNumberSearchText,
      leadStates,
      types,
      products,
      sortField,
      sortOrder
    ],
    async () => {
      // Simuliere eine Verzögerung von 500ms
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Erstelle Mock-Daten für die Lead-Übersicht
      const mockData: LeadsQueryResultDto = {
        leadOverviews: [
          {
            leadId: "lead1",
            firstName: "Max",
            lastName: "Mustermann",
            type: 1, // Beispielwert, der einem bestimmten LeadType entspricht
            product: Product.NursingService, // Beispiel: Betreutes Wohnen
            city: "Mainz",
            zipCode: "55116",
            leadNumber: "ANF-001",
            link: "lead1-link",
            state: 1, // Beispiel-Status
            createdDateUtc: "", // placeholder, wird später gesetzt
            assignedTo: "Peter Meier",
            tasks: 1,
            phase: "In Bearbeitung",
            priority: ""
          },
          {
            leadId: "lead2",
            firstName: "Erika",
            lastName: "Musterfrau",
            type: 1,
            product: Product.ShortTermCare, // Beispiel: Kurzzeitpflege
            city: "Hamburg",
            zipCode: "20095",
            leadNumber: "ANF-002",
            link: "lead2-link",
            state: 1,
            createdDateUtc: "",
            assignedTo: "Petra Schmidt",
            tasks: 0,
            phase: "Gewonnen",
            priority: ""
          },
          {
            leadId: "lead3",
            firstName: "Hans",
            lastName: "Müller",
            type: 1,
            product: Product._24HoursCare, // Beispiel: Tagespflege
            city: "München",
            zipCode: "80331",
            leadNumber: "ANF-003",
            link: "lead3-link",
            state: 1,
            createdDateUtc: "",
            assignedTo: "Maria Müller",
            tasks: 3,
            phase: "Geschlossen",
            priority: ""
          },
          {
            leadId: "lead4",
            firstName: "Anna",
            lastName: "Schmidt",
            type: 1,
            product: Product.StepChairLift, // Beispiel: Hospiz
            city: "Frankfurt",
            zipCode: "60311",
            leadNumber: "ANF-004",
            link: "lead4-link",
            state: 0,
            createdDateUtc: "",
            assignedTo: "Hans Schmidt",
            tasks: 0,
            phase: "Neu",
            priority: ""
          },
          {
            leadId: "lead5",
            firstName: "Peter",
            lastName: "Schneider",
            type: 5,
            product: Product.PropertiesRetirement, // Beispiel: Stationäre Pflege
            city: "Stuttgart",
            zipCode: "70173",
            leadNumber: "ANF-005",
            link: "lead5-link",
            state: 5,
            createdDateUtc: "",
            assignedTo: "Anna Becker",
            tasks: 3,
            phase: "Gewonnen",
            priority: ""
          },
          {
            leadId: "lead6",
            firstName: "Laura",
            lastName: "Fischer",
            type: 1,
            product: Product.AssistedLivingCare, // Beispiel: Unbekannt
            city: "Düsseldorf",
            zipCode: "40210",
            leadNumber: "ANF-006",
            link: "lead6-link",
            state: 1,
            createdDateUtc: "",
            assignedTo: "Michael Meyer",
            tasks: 0,
            phase: "Gewonnen",
            priority: ""
          },
          {
            leadId: "lead7",
            firstName: "Thomas",
            lastName: "Weber",
            type: 1,
            product: Product.BathRennovation, // Beispiel: Betreutes Wohnen
            city: "Köln",
            zipCode: "50667",
            leadNumber: "ANF-007",
            link: "lead7-link",
            state: 1,
            createdDateUtc: "",
            assignedTo: "Peter Meier",
            tasks: 2,
            phase: "Gewonnen",
            priority: ""
          },
          {
            leadId: "lead8",
            firstName: "Sophie",
            lastName: "Hoffmann",
            type: 1,
            product: Product.ShortTermCare, // Beispiel: Kurzzeitpflege
            city: "Leipzig",
            zipCode: "04109",
            leadNumber: "ANF-008",
            link: "lead8-link",
            state: 2,
            createdDateUtc: "",
            assignedTo: "Petra Schmidt",
            tasks: 0,
            phase: "In Bearbeitung",
            priority: ""
          },
          {
            leadId: "lead9",
            firstName: "Lukas",
            lastName: "Schmidt",
            type: 1,
            product: Product.DayCare, // Beispiel: Tagespflege
            city: "Dortmund",
            zipCode: "44135",
            leadNumber: "ANF-009",
            link: "lead9-link",
            state: 3,
            createdDateUtc: "",
            assignedTo: "Maria Müller",
            tasks: 2,
            phase: "In Bearbeitung",
            priority: ""
          },
          {
            leadId: "lead10",
            firstName: "Julia",
            lastName: "Schneider",
            type: 1,
            product: Product.Hospice, // Beispiel: Hospiz
            city: "Essen",
            zipCode: "45127",
            leadNumber: "ANF-010",
            link: "lead10-link",
            state: 4,
            createdDateUtc: "",
            assignedTo: "Hans Schmidt",
            tasks: 0,
            phase: "In Bearbeitung",
            priority: ""
          }
        ],
        totalDataCount: 10
      };

      // Verteilen der Erstellungsdaten über die letzten 14 Tage:
      // Der erste Lead (Index 0) bekommt das neueste Datum (heute), 
      // und der letzte Lead bekommt ein Datum von vor 14 Tagen.
      const leadCount = mockData.leadOverviews.length;
      const twoWeeksInMs = 14 * 24 * 60 * 60 * 1000;
      mockData.leadOverviews = mockData.leadOverviews.map((lead, index) => {
        // Berechne den proportionalen Offset
        const offsetMs = (index / (leadCount - 1)) * twoWeeksInMs;
        lead.createdDateUtc = new Date(Date.now() - offsetMs).toISOString();
        return lead;
      });

      return mockData;
    },
    { ...options }
  );
};

export const useGetAllProduct = () => {
  return useQuery(
    [GetAllLeadProducts],
    async () => {
      // Simuliere eine Verzögerung von 500ms
      await new Promise((resolve) => setTimeout(resolve, 500));
      // Erstelle einen Mock-Datensatz für Produkte
      const mockProducts: Product[] = [
        Product.StationaryCare,
        Product.Hospice,
        Product.AssistedLivingCare,
        Product.ShortTermCare,
        Product.DayCare
      ];
      return mockProducts;
    },
    { staleTime: 1000 * 60 * 5 }
  );
};
